import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Button, List, Modal, Tag, Typography } from 'antd';

import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import { Icon } from '@eduzz/ui-antd-theme';

import AlpaClass from './Form/Alpaclass';
import File from './Form/File';
import Nutror from './Form/Nutror';
import Other from './Form/Other';
import DeliveryList from './List';
import Validations from './validations';
import { FormContent } from '../../EditProduct/components/FormContent';

interface IProps {
  deliveries: any;
  onChange?: any;
  fade?: any;
  loading?: boolean;
  onChangeRequiredFields?: any;
  isInfo?: boolean;
  offer?: any;
  ref?: any;
  newProduct?: boolean;
  isService?: boolean;
  enabledValidation?: boolean;
}

const ifHasNutror = (items: any[]) => (items || []).some(i => i.method_id == 24);
const ifHasHotmartFormat = (items: any[]) => (items || []).some(i => i.method_id == 4);

const Delivery: React.FC<IProps> = ({
  deliveries: deliveriesProp,
  onChange,
  loading,
  onChangeRequiredFields,
  isInfo,
  offer,
  newProduct,
  isService,
  enabledValidation
}) => {
  const [deliveries, setDeliveries] = useState<any>(deliveriesProp);
  const [modal, setModal] = useState(false);
  const [modalFile, setModalFile] = useState(false);
  const [modalNutror, setModalNutror] = useState(false);
  const [modalOther, setModalOther] = useState(false);
  const [modalAlpaClass, setModalAlpaClass] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any>([]);
  const prevPropsRef = useRef({ enabledValidation, deliveries: deliveriesProp });

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const changeState = useCallback(() => {
    const newValidationErrors = run({ deliveries: deliveriesProp }, Validations);
    setDeliveries(deliveriesProp);
    setValidationErrors(newValidationErrors);
    if (onChangeRequiredFields)
      onChangeRequiredFields(
        'deliveryRequiredFields',
        newValidationErrors ? Object.keys(newValidationErrors).length : 0
      );
  }, [deliveriesProp, onChangeRequiredFields]);

  useEffect(() => {
    if (!loading && enabledValidation) changeState(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const prevProps = prevPropsRef.current;
    if (
      (!prevProps.enabledValidation && enabledValidation) ||
      (enabledValidation && prevProps.deliveries !== deliveriesProp)
    )
      changeState();
    prevPropsRef.current = { enabledValidation, deliveries: deliveriesProp };
  }, [enabledValidation, deliveriesProp, changeState]);

  const toggleModal = useCallback(() => {
    setModal(prev => !prev);
  }, []);
  const toggleModalFile = useCallback(() => {
    setModalFile(prev => !prev);
  }, []);
  const toggleModalNutror = useCallback(() => {
    setModalNutror(prev => !prev);
  }, []);
  const toggleModalOther = useCallback(() => {
    setModalOther(prev => !prev);
  }, []);
  const toggleModalAlpaClass = useCallback(() => {
    setModalAlpaClass(prev => !prev);
  }, []);

  const selectDelivery = useCallback((delivery: 'FILES' | 'NUTROR' | 'OTHERS' | 'ALPACLASS' | 'PIN') => {
    switch (delivery) {
      case 'FILES':
        setModal(false);
        setModalFile(true);
        break;
      case 'NUTROR':
        setModal(false);
        setModalNutror(true);
        break;
      case 'OTHERS':
        setModal(false);
        setModalOther(true);
        break;
      case 'ALPACLASS':
        setModal(false);
        setModalAlpaClass(true);
        break;
      default:
    }
  }, []);

  const onChangeDeliveries = useCallback(
    (dels: any) => {
      if (!onChange) return;
      let files: any = {};
      let nutror: any = {};
      let others: any = [];
      let newDeliveries: any = [];
      const newFiles = dels?.length ? dels.filter((item: any) => item.method_id === '5') : false;
      let filesId: any = [];

      for (let i = 0; i < newFiles.length; i++) {
        const nf = newFiles[i];

        if (nf.files?.length) {
          filesId = filesId.concat(
            nf.files.map((fil: any) => (fil?.upload_id ? { upload_id: fil.upload_id, name: fil.name } : fil))
          );
        } else {
          filesId = filesId?.length ? filesId : false;
        }
      }

      if (Array.isArray(filesId) && filesId.length) {
        files.method_id = '5';
        files.files = filesId;
      } else {
        files = false;
      }

      nutror = ifHasNutror(dels) ? dels.filter((item: any) => item.method_id === '24')[0] : false;
      others = dels?.length ? dels.filter((item: any) => item.method_id !== '5' && item.method_id !== '24') : false;
      if (files) newDeliveries.push(files);
      if (nutror) newDeliveries.push(nutror);
      if (others) newDeliveries = newDeliveries.concat(others);
      onChange(newDeliveries);
    },
    [onChange]
  );

  const onEditDeliveries = useCallback(
    (dels: any) => {
      setDeliveries(dels);
      onChangeDeliveries(dels);
    },
    [onChangeDeliveries]
  );
  const onAddNewItem = useCallback(
    (item: any) => {
      const cd = deliveries && Array.isArray(deliveries) ? deliveries : [];
      const nd = [...cd, item];
      setDeliveries(nd);
      setModalFile(false);
      setModalOther(false);
      setModalNutror(false);
      onChangeDeliveries(nd);
    },
    [deliveries, onChangeDeliveries]
  );

  const showOtherDeliveries = useCallback(() => {
    const isProductFree = offer.charge_type === 'G';
    const isBeta = eduzzAccountsService.verifyUserHasBetaModule('allow_all_deliveries_for_free_charge_product');
    const showAllDeliveries = true;
    return !isProductFree || (newProduct && isBeta) || showAllDeliveries;
  }, [offer, newProduct]);

  const title = isService ? 'Entregas de arquivo (opcional)' : 'Entregas de produto';

  return (
    <FormContent
      title={title}
      loading={loading}
      action={
        <span className='inline-flex leading-none'>
          <Button
            id='product-add-delivery'
            color='default'
            variant='filled'
            className='m-1'
            onClick={toggleModal}
            icon={<Icon.PackagePlus size={16} className='size-4' />}
          >
            Adicionar Entrega
          </Button>
        </span>
      }
    >
      <>
        <div className='flex flex-col gap-4'>
          {isInfo && !isService && isValid('deliveries') && errorFor('deliveries') && (
            <Typography.Text type='danger'>Obrigatório pelo menos uma entrega cadastrada.</Typography.Text>
          )}
          {isService && (
            <Typography.Text type='secondary'>
              Para vendas do tipo Serviço, você pode adicionar uma entrega se desejar, mas não é obrigatório.
            </Typography.Text>
          )}
          <DeliveryList deliveries={deliveries} onEditSomething={onEditDeliveries} offer={offer} />
        </div>

        <Modal
          open={modal}
          onCancel={toggleModal}
          width={530}
          height={440}
          footer={
            <div className='flex flex-wrap items-center justify-end gap-2'>
              <Button onClick={toggleModal} type='text'>
                Fechar
              </Button>
            </div>
          }
          title='Tipo de entrega'
        >
          {!showOtherDeliveries() && (
            <Alert
              type='warning'
              message='Para produtos gratuitos só é possível utilizar as entregas fornecidas pela Eduzz'
              className='mb-2'
            />
          )}
          <List className='mt-4'>
            <List.Item className='cursor-pointer' onClick={() => selectDelivery('FILES')} id='product-delivery-files'>
              <List.Item.Meta
                avatar={
                  <div className='flex size-10 items-center justify-center'>
                    <Icon.FileText size={28} className='size-7' />
                  </div>
                }
                title='Arquivos'
                description={"PDF's, documentos, imagens e e-books."}
              />
            </List.Item>
            {!isService && (
              <>
                {!ifHasNutror(deliveries) && (
                  <List.Item
                    className='cursor-pointer'
                    onClick={() => selectDelivery('NUTROR')}
                    id='product-delivery-nutror'
                  >
                    <List.Item.Meta
                      avatar={
                        <div className='flex size-10 shrink-0 items-center justify-center'>
                          <img src='/img/eduzz-icon.svg' alt='' className='h-10 w-full object-contain' />
                        </div>
                      }
                      title='Eduzz Membros'
                      description='Nossa plataforma de membros.'
                    />
                  </List.Item>
                )}
                <List.Item
                  className='cursor-pointer'
                  onClick={() => selectDelivery('ALPACLASS')}
                  id='product-delivery-alpaclass'
                >
                  <List.Item.Meta
                    avatar={
                      <div className='flex size-10 items-center justify-center'>
                        <img src='/img/alpaclass.svg' width='40' height='40' alt='' />
                      </div>
                    }
                    title={
                      <>
                        Alpaclass <Tag color='success'>Novo</Tag>
                      </>
                    }
                    description='Plataforma de área de membros.'
                  />
                </List.Item>
                {showOtherDeliveries() && (
                  <List.Item
                    className='cursor-pointer'
                    onClick={() => selectDelivery('OTHERS')}
                    id='product-delivery-others'
                  >
                    <List.Item.Meta
                      avatar={
                        <div className='flex size-10 items-center justify-center'>
                          <Icon.SlidersHorizontal size={28} className='size-7' />
                        </div>
                      }
                      title='Outros'
                      description='Weve, customizado e outros.'
                    />
                  </List.Item>
                )}
              </>
            )}
          </List>
        </Modal>

        <File isModal={true} open={modalFile} onClose={toggleModalFile} onOk={onAddNewItem} />
        <Nutror isModal={true} open={modalNutror} onClose={toggleModalNutror} onOk={onAddNewItem} />
        <Other
          isModal={true}
          deliveries={deliveriesProp}
          open={modalOther}
          newProduct={newProduct}
          onClose={toggleModalOther}
          onOk={onAddNewItem}
          hasHotmart={ifHasHotmartFormat(deliveries)}
        />
        <AlpaClass
          isModal={true}
          deliveries={deliveriesProp}
          open={modalAlpaClass}
          newProduct={newProduct}
          onClose={toggleModalAlpaClass}
          onOk={onAddNewItem}
          offer={offer}
        />
      </>
    </FormContent>
  );
};

export default Delivery;
